import { useState, useMemo, Fragment } from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Collapse from '@mui/material/Collapse';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { BreadcrumbHeader, DataTable, EmptyState, ActionMenu, ConfirmModal, FilterTabs } from '../../components/shared';
import { users as initialUsers, customers as initialCustomers } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Constants ────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const ROLE_OPTIONS = ['Admin', 'Dispatcher', 'Warehouse Staff', 'Read Only', 'Customer'];

const ROLE_COLORS = {
  'Admin': { bg: '#EDE7F6', color: '#4527A0' },
  'Dispatcher': { bg: '#E3F2FD', color: '#1565C0' },
  'Warehouse Staff': { bg: '#E0F2F1', color: '#00695C' },
  'Read Only': { bg: '#ECEFF1', color: '#546E7A' },
  'Customer': { bg: '#FFF3E0', color: '#E65100' }
};

const AVATAR_COLORS = ['#1565C0', '#00695C', '#4527A0', '#E65100', '#2E7D32', '#795548'];

const PERMISSION_ROWS = [
'View Dashboard',
'Manage Orders',
'Manage Shipments',
'Manage Inventory',
'View Accounting',
'Manage Accounting',
'Manage Settings',
'Invite Users',
'Access Portal'];

// Indexed to match PERMISSION_ROWS order
const PERMISSION_MATRIX = {
  'Admin': [true, true, true, true, true, true, true, true, false],
  'Dispatcher': [true, true, true, true, true, false, false, false, false],
  'Warehouse Staff': [true, false, false, true, false, false, false, false, false],
  'Read Only': [true, false, false, false, true, false, false, false, false],
  'Customer': [false, false, false, false, false, false, false, false, true]
};

const EMPTY_INVITE = { name: '', email: '', role: 'Dispatcher', sendInvite: true, customerName: '' };
const EMPTY_EDIT = { name: '', email: '', role: 'Dispatcher', active: true, customerName: '' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getAvatarColor(name) {
  const hash = [...(name || '')].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function getInitials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function relativeTime(dateStr) {
  if (!dateStr) return 'Never';
  const diff = dayjs().diff(dayjs(dateStr), 'day');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 30) return `${diff} days ago`;
  return dayjs(dateStr).format('MMM D, YYYY');
}

// ─── Role badge ───────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const cfg = ROLE_COLORS[role] ?? { bg: '#ECEFF1', color: '#546E7A' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '10px', py: '3px', borderRadius: '12px', backgroundColor: cfg.bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: cfg.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {role}
      </Typography>
    </Box>);

}

// ─── User avatar ──────────────────────────────────────────────────────────────
function UserAvatar({ name }) {
  return (
    <Box
      sx={{ width: 28, height: 28, borderRadius: '50%',
        backgroundColor: getAvatarColor(name),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0
      }}>
      
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
        {getInitials(name)}
      </Typography>
    </Box>);

}

// ─── Role permissions card ────────────────────────────────────────────────────
function PermissionsCard() {
  const [open, setOpen] = useState(false);
  const roles = Object.keys(PERMISSION_MATRIX);

  return (
    <ContentCard sx={{ overflow: 'hidden' }}>
      {/* Header / toggle */}
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: '14px', cursor: 'pointer',
          '&:hover': { backgroundColor: '#f8fafc' }
        }}>
        
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          Role Permissions
        </Typography>
        {open ?
        <ExpandLessIcon sx={{ fontSize: 20, color: 'var(--grey-500)' }} /> :
        <ExpandMoreIcon sx={{ fontSize: 20, color: 'var(--grey-500)' }} />
        }
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 3, pb: 3, overflow: 'auto' }}>
          {/* Grid: label col + 4 role cols */}
          <Box
            sx={{ display: 'grid',
              gridTemplateColumns: `220px repeat(${roles.length}, 1fr)`,
              gap: 0,
              minWidth: 500
            }}>
            
            {/* Header row */}
            <Box sx={{ py: '8px', borderBottom: '1px solid var(--grey-200)' }} />
            {roles.map((role) =>
            <Box key={`header-${role}`} sx={{ py: '8px', borderBottom: '1px solid var(--grey-200)', textAlign: 'center' }}>
                <RoleBadge role={role} />
              </Box>
            )}
            {/* Permission rows */}
            {PERMISSION_ROWS.map((perm, rowIdx) => {
              const borderBottom = rowIdx < PERMISSION_ROWS.length - 1 ? '1px solid var(--grey-200)' : 'none';
              return (
                <Fragment key={perm}>
                  <Box sx={{ py: '10px', borderBottom, pr: 2 }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-700)' }}>{perm}</Typography>
                  </Box>
                  {roles.map((role) => {
                    const allowed = PERMISSION_MATRIX[role][rowIdx];
                    return (
                      <Box
                        key={`${role}-${perm}`}
                        sx={{ py: '10px', borderBottom, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        
                        {allowed ?
                        <CheckIcon sx={{ fontSize: 16, color: '#2E7D32' }} /> :
                        <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-300)' }}>—</Typography>
                        }
                      </Box>);

                  })}
                </Fragment>);

            })}
          </Box>
        </Box>
      </Collapse>
    </ContentCard>);

}

// ─── Invite modal ─────────────────────────────────────────────────────────────
function InviteModal({ open, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_INVITE);
  const [custTypeFilter, setCustTypeFilter] = useState('All');

  const handleEnter = () => {setForm(EMPTY_INVITE);setCustTypeFilter('All');};
  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const isValid = form.name && form.email && form.role && (form.role !== 'Customer' || form.customerName);

  const filteredCustomers = useMemo(
    () => initialCustomers.filter((c) => c.active && (custTypeFilter === 'All' || c.customerType === custTypeFilter)),
    [custTypeFilter]
  );

  return (
    <Dialog open={open} onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Invite User
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <TextField
          size="small" label="Full Name" value={form.name} onChange={set('name')}
          fullWidth required sx={fieldSx} />
        
        <TextField
          size="small" label="Email" type="email" value={form.email} onChange={set('email')}
          fullWidth required sx={fieldSx} />

        <FormControl size="small" required sx={fieldSx}>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={form.role}
            onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
            sx={{ fontSize: '0.875rem' }}>
            
            {ROLE_OPTIONS.map((r) =>
            <MenuItem key={r} value={r} sx={{ fontSize: '0.875rem' }}>{r}</MenuItem>
            )}
          </Select>
        </FormControl>

        {form.role === 'Customer' &&
        <>
            {/* Customer type filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)', flexShrink: 0 }}>Filter by type:</Typography>
              <ToggleButtonGroup
              exclusive size="small"
              value={custTypeFilter}
              onChange={(_, val) => {if (val) {setCustTypeFilter(val);setForm((prev) => ({ ...prev, customerName: '' }));}}}
              sx={{ '& .MuiToggleButton-root': {
                  textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, px: '10px', py: '3px',
                  borderColor: 'var(--grey-200)', color: 'var(--grey-500)',
                  '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                  '&.Mui-selected:hover': { backgroundColor: '#00796B' }
                }
              }}>
              
                <ToggleButton value="All">All</ToggleButton>
                <ToggleButton value="End-Customer">End-Customer</ToggleButton>
                <ToggleButton value="Reseller">Reseller</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <FormControl size="small" fullWidth required sx={fieldSx}>
              <InputLabel>Customer</InputLabel>
              <Select
              label="Customer"
              value={form.customerName}
              onChange={(e) => setForm((prev) => ({ ...prev, customerName: e.target.value }))}
              sx={{ fontSize: '0.875rem' }}>
              
                {filteredCustomers.map((c) =>
              <MenuItem key={c.id} value={c.name} sx={{ fontSize: '0.875rem' }}>{c.name}</MenuItem>
              )}
              </Select>
            </FormControl>
          </>
        }

        <FormControlLabel
          control={
          <Switch
            checked={form.sendInvite}
            onChange={(e) => setForm((prev) => ({ ...prev, sendInvite: e.target.checked }))}
            size="small"
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' } }} />

          }
          label={<Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>Send invite email</Typography>} />
        
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained" size="small"
          disabled={!isValid}
          onClick={() => onSave(form)}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2
          }}>
          
          Send Invite
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Edit modal ───────────────────────────────────────────────────────────────
function EditModal({ open, initial, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_EDIT);
  const [custTypeFilter, setCustTypeFilter] = useState('All');

  const handleEnter = () => {
    if (initial) {
      setForm({ name: initial.name, email: initial.email, role: initial.role, active: initial.active, customerName: initial.customerName ?? '' });
      setCustTypeFilter('All');
    }
  };

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const isValid = form.name && form.email && form.role && (form.role !== 'Customer' || form.customerName);

  const filteredCustomers = useMemo(
    () => initialCustomers.filter((c) => c.active && (custTypeFilter === 'All' || c.customerType === custTypeFilter)),
    [custTypeFilter]
  );

  return (
    <Dialog open={open} onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Edit User
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <TextField
          size="small" label="Full Name" value={form.name} onChange={set('name')}
          fullWidth required sx={fieldSx} />
        
        <TextField
          size="small" label="Email" type="email" value={form.email} onChange={set('email')}
          fullWidth required sx={fieldSx} />

        <FormControl size="small" required sx={fieldSx}>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={form.role}
            onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
            sx={{ fontSize: '0.875rem' }}>
            
            {ROLE_OPTIONS.map((r) =>
            <MenuItem key={r} value={r} sx={{ fontSize: '0.875rem' }}>{r}</MenuItem>
            )}
          </Select>
        </FormControl>

        {form.role === 'Customer' &&
        <>
            {/* Customer type filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)', flexShrink: 0 }}>Filter by type:</Typography>
              <ToggleButtonGroup
              exclusive size="small"
              value={custTypeFilter}
              onChange={(_, val) => {if (val) {setCustTypeFilter(val);setForm((prev) => ({ ...prev, customerName: '' }));}}}
              sx={{ '& .MuiToggleButton-root': {
                  textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, px: '10px', py: '3px',
                  borderColor: 'var(--grey-200)', color: 'var(--grey-500)',
                  '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                  '&.Mui-selected:hover': { backgroundColor: '#00796B' }
                }
              }}>
              
                <ToggleButton value="All">All</ToggleButton>
                <ToggleButton value="End-Customer">End-Customer</ToggleButton>
                <ToggleButton value="Reseller">Reseller</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <FormControl size="small" fullWidth required sx={fieldSx}>
              <InputLabel>Customer</InputLabel>
              <Select
              label="Customer"
              value={form.customerName}
              onChange={(e) => setForm((prev) => ({ ...prev, customerName: e.target.value }))}
              sx={{ fontSize: '0.875rem' }}>
              
                {filteredCustomers.map((c) =>
              <MenuItem key={c.id} value={c.name} sx={{ fontSize: '0.875rem' }}>{c.name}</MenuItem>
              )}
              </Select>
            </FormControl>
          </>
        }

        {/* Status toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>Status</Typography>
          <ToggleButtonGroup
            exclusive size="small"
            value={form.active ? 'active' : 'inactive'}
            onChange={(_, val) => {if (val) setForm((prev) => ({ ...prev, active: val === 'active' }));}}
            sx={{ '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, px: 2, py: '4px',
                borderColor: 'var(--grey-200)', color: 'var(--grey-500)',
                '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                '&.Mui-selected:hover': { backgroundColor: '#00796B' }
              }
            }}>
            
            <ToggleButton value="active">Active</ToggleButton>
            <ToggleButton value="inactive">Inactive</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained" size="small"
          disabled={!isValid}
          onClick={() => onSave(form)}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2
          }}>
          
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function UsersPage() {
  const [users, setUsers] = useState(initialUsers);
  const [activeTab, setActiveTab] = useState('internal');
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState('deactivate'); // 'deactivate' | 'reset'
  const [confirmTarget, setConfirmTarget] = useState(null);

  const [banner, setBanner] = useState('');

  const showBanner = (msg) => {setBanner(msg);setTimeout(() => setBanner(''), 4000);};

  const handleSort = (field) =>
  setSortConfig((prev) => ({
    field,
    direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const internalCount = useMemo(() => users.filter((u) => u.active && u.role !== 'Customer').length, [users]);
  const customerCount = useMemo(() => users.filter((u) => u.active && u.role === 'Customer').length, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...users]
    .filter((u) => u.role === 'Customer' ? activeTab === 'customer' : activeTab === 'internal')
    .filter((u) => showInactive ? !u.active : u.active)
    .filter((u) =>
    !q ||
    u.name.toLowerCase().includes(q) ||
    u.email.toLowerCase().includes(q) ||
    u.role.toLowerCase().includes(q) ||
    (u.customerName ?? '').toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const av = a[sortConfig.field] ?? '';
      const bv = b[sortConfig.field] ?? '';
      const cmp = typeof av === 'boolean' ?
      av === bv ? 0 : av ? -1 : 1 :
      String(av).localeCompare(String(bv));
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [users, activeTab, showInactive, search, sortConfig]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleInviteSave = (form) => {
    const newUser = {
      id: `usr-new-${Date.now()}`,
      code: `USR-${String(users.length + 1).padStart(2, '0')}`,
      name: form.name,
      email: form.email,
      role: form.role,
      active: true,
      lastLoginDate: null,
      customerName: form.role === 'Customer' ? form.customerName : null
    };
    setUsers((prev) => [...prev, newUser]);
    setInviteOpen(false);
    showBanner(form.sendInvite ?
    `Invite sent to ${form.email}` :
    `User ${form.name} added successfully`
    );
  };

  const handleEditSave = (form) => {
    setUsers((prev) =>
    prev.map((u) => u.id === editTarget.id ?
    { ...u, name: form.name, email: form.email, role: form.role, active: form.active, customerName: form.role === 'Customer' ? form.customerName : null } :
    u
    )
    );
    setEditOpen(false);
    showBanner('User updated successfully.');
  };

  const handleDeactivateConfirm = () => {
    setUsers((prev) =>
    prev.map((u) => u.id === confirmTarget.id ? { ...u, active: false } : u)
    );
    setConfirmOpen(false);
    setConfirmTarget(null);
  };

  const handleResetConfirm = () => {
    setConfirmOpen(false);
    showBanner(`Password reset email sent to ${confirmTarget.email}`);
    setConfirmTarget(null);
  };

  const handleActivate = (user) => {
    setUsers((prev) =>
    prev.map((u) => u.id === user.id ? { ...u, active: true } : u)
    );
    showBanner(`${user.name} activated successfully.`);
  };

  const openDeactivate = (user) => {
    setConfirmType('deactivate');
    setConfirmTarget(user);
    setConfirmOpen(true);
  };

  const openReset = (user) => {
    setConfirmType('reset');
    setConfirmTarget(user);
    setConfirmOpen(true);
  };

  // ── Shared column builders ───────────────────────────────────────────────────
  const nameCol = {
    field: 'name',
    headerName: 'Name',
    sortable: true,
    renderCell: (row) =>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <UserAvatar name={row.name} />
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
          {row.name}
        </Typography>
      </Box>

  };

  const emailCol = {
    field: 'email',
    headerName: 'Email',
    sortable: true,
    renderCell: (row) =>
    <Typography
      component="a"
      href={`mailto:${row.email}`}
      onClick={(e) => e.stopPropagation()}
      sx={{ fontSize: '0.875rem', color: '#009688', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      
        {row.email}
      </Typography>

  };

  const lastLoginCol = {
    field: 'lastLoginDate',
    headerName: 'Last Login',
    sortable: true,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>
        {relativeTime(row.lastLoginDate)}
      </Typography>

  };

  const actionsCol = {
    field: '_actions',
    headerName: '',
    sortable: false,
    renderCell: (row) => {
      const actions = [
      { label: 'Edit', onClick: () => {setEditTarget(row);setEditOpen(true);} },
      { label: 'Reset Password', onClick: () => openReset(row) },
      ...(row.active ?
      [{ label: 'Deactivate', onClick: () => openDeactivate(row) }] :
      [{ label: 'Activate', onClick: () => handleActivate(row) }])];

      return (
        <Box onClick={(e) => e.stopPropagation()}>
          <ActionMenu actions={actions} hideUntilRowHover />
        </Box>);

    }
  };

  // ── Internal users columns ───────────────────────────────────────────────────
  const internalColumns = [
  nameCol,
  emailCol,
  {
    field: 'role',
    headerName: 'Role',
    sortable: true,
    renderCell: (row) => <RoleBadge role={row.role} />
  },
  lastLoginCol,
  actionsCol];

  // ── Customer users columns ───────────────────────────────────────────────────
  const customerColumns = [
  nameCol,
  emailCol,
  {
    field: 'customerName',
    headerName: 'Company',
    sortable: true,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>
          {row.customerName ?? '—'}
        </Typography>

  },
  {
    field: '_custType',
    headerName: 'Type',
    sortable: false,
    renderCell: (row) => {
      const cust = initialCustomers.find((c) => c.name === row.customerName);
      if (!cust) return <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>;
      const isReseller = cust.customerType === 'Reseller';
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center',
          px: '8px', py: '3px', borderRadius: '12px',
          backgroundColor: isReseller ? '#E0F2F1' : '#EDE7F6'
        }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1,
            color: isReseller ? '#00695C' : '#512DA8',
            whiteSpace: 'nowrap'
          }}>
              {cust.customerType}
            </Typography>
          </Box>);

    }
  },
  lastLoginCol,
  actionsCol];

  const columns = activeTab === 'internal' ? internalColumns : customerColumns;

  // ── Confirm modal content ────────────────────────────────────────────────────
  const confirmTitle = confirmType === 'reset' ? 'Reset Password' : 'Deactivate User';
  const confirmMessage = confirmType === 'reset' ?
  `Send a password reset email to ${confirmTarget?.email}?` :
  `Are you sure you want to deactivate ${confirmTarget?.name}? They will lose access immediately.`;
  const confirmLabel = confirmType === 'reset' ? 'Send Reset Email' : 'Deactivate';
  const handleConfirm = confirmType === 'reset' ? handleResetConfirm : handleDeactivateConfirm;

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {banner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {banner}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Users"
        crumbs={[{ label: 'Settings' }, { label: 'Users' }]} />

      {/* Role permissions card */}
      <PermissionsCard />

      <ContentCard sx={{ overflow: 'hidden' }}>
        {/* Tabs */}
        <Box sx={{ px: 3, pt: 2, '& > div': { mb: 0 } }}>
          <FilterTabs
            tabs={[
            { label: `Internal Users (${internalCount})`, value: 'internal' },
            { label: `Customer Users (${customerCount})`, value: 'customer' }]
            }
            value={activeTab}
            onChange={(val) => {setActiveTab(val);setSearch('');setShowInactive(false);}} />
          
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
          <TextField
              size="small"
              placeholder="Search by Name, Email, or Role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment:
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                </InputAdornment>

              }}
              sx={{ minWidth: 400, maxWidth: 600, backgroundColor: 'var(--paper)', borderRadius: 1, ...fieldSx }} />
            
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
            <FormControlLabel
                control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  size="small"
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' }
                  }} />

                }
                label={<Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', whiteSpace: 'nowrap' }}>Show Inactive</Typography>}
                sx={{ ml: 0 }} />
              
            <Button
                variant="contained" size="small"
                startIcon={<AddIcon />}
                onClick={() => setInviteOpen(true)}
                sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
                  textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none'
                }}>
                
              Invite User
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
          {filtered.length === 0 ?
            <EmptyState title="No users found" description="Try adjusting your filter or search." /> :

            <DataTable
              columns={columns}
              data={filtered}
              sortConfig={sortConfig}
              onSort={handleSort} />

            }
        </Box>
        </Box>
      </ContentCard>

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} onSave={handleInviteSave} />
      <EditModal open={editOpen} initial={editTarget} onClose={() => setEditOpen(false)} onSave={handleEditSave} />

      <ConfirmModal
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        onConfirm={handleConfirm}
        onCancel={() => {setConfirmOpen(false);setConfirmTarget(null);}} />
      
    </Box>);

}